import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Inventory2 as InventoryIcon,
  Pin as PinIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { ProductoLote } from '../../services/loteService';
import GranjaArticuloCodigoField from '../../components/granja/GranjaArticuloCodigoField';
import { getApiErrorMessage } from '../../utils/apiError';

const LotesPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ProductoLote[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [nroLote, setNroLote] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await loteService.search({
        codigoArticulo: codigoArticulo || undefined,
        nroLote: nroLote || undefined,
        page: page + 1,
        pageSize,
        soloConSaldo: false,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar lotes'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InventoryIcon sx={{ color: '#1976d2', fontSize: 36 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Lotes
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<PinIcon />}
              onClick={() => navigate('/inventario/lotes/numeracion')}
            >
              Máscaras
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems={{ sm: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <GranjaArticuloCodigoField
                label="Artículo"
                value={codigoArticulo}
                onChange={setCodigoArticulo}
                dialogTitle="Buscar artículo"
              />
            </Box>
            <TextField
              label="Nro lote / serie"
              size="small"
              value={nroLote}
              onChange={(e) => setNroLote(e.target.value)}
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(0);
                  load();
                }
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' },
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                setPage(0);
                load();
              }}
              disabled={loading}
              sx={{ minWidth: 120, borderRadius: 2, mt: { sm: 0.5 } }}
            >
              Buscar
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Lote / serie</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vencimiento</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Saldo
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((l) => (
                    <TableRow
                      key={l.idLote}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/inventario/lotes/${l.idLote}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {l.codigoArticulo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {l.descripcionArticulo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" fontFamily="monospace">
                            {l.nroLote}
                          </Typography>
                          {l.esSerie && <Chip size="small" label="Serie" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {l.fechaVencimiento ? String(l.fechaVencimiento).slice(0, 10) : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={l.estado === 'B' ? 'Bloqueado' : 'Activo'}
                          color={l.estado === 'B' ? 'default' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="right">{l.cantidadTotal}</TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                          Sin resultados. Probá otro filtro. Los lotes se generan al ingresar stock
                          (remito, ajuste, etc.).
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default LotesPage;
